import { Component, Input, OnChanges, ChangeDetectionStrategy } from '@angular/core';
import { IDiagnosis } from 'src/app/core/domain/patient';
import { DiagnosisNameEnum, DiagnosisTypeEnum } from 'src/app/core/domain/diagnosis';
import { CommonModule } from '@angular/common';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslateModule, TranslateService } from '@ngx-translate/core';

interface Badge {
  src: string;
  alt: string;
  tooltipKey: string;
}

// map each diagnosis_name → badge
const NAME_BADGES: Record<DiagnosisNameEnum, Badge> = {
  [DiagnosisNameEnum.RETT_SYNDROME]: {
    src: 'assets/badges/rett-syndrome.svg',
    alt: 'Rett syndrome',
    tooltipKey: 'BADGE.TOOLTIP.RETT_SYNDROME'
  },
  [DiagnosisNameEnum.RETT_LIKE_SYNDROME]: {
    src: 'assets/badges/rett-like.svg',
    alt: 'Rett-like syndrome',
    tooltipKey: 'BADGE.TOOLTIP.RETT_LIKE'
  },
  [DiagnosisNameEnum.MECP2_DUPLICATION]: {
    src: 'assets/badges/mecp2-duplication.svg',
    alt: 'MECP2 duplication Syndrome',
    tooltipKey: 'BADGE.TOOLTIP.MECP2_DUPLICATION'
  }
};

// map each diagnosis_type → badge
const TYPE_BADGES: Record<DiagnosisTypeEnum, Badge> = {
  [DiagnosisTypeEnum.MECP2_MUTATION]: {
    src: 'assets/badges/mecp2-mutation.svg',
    alt: 'MECP2 mutation',
    tooltipKey: 'BADGE.TOOLTIP.MECP2_MUTATION'
  },
  [DiagnosisTypeEnum.FOXG1_MUTATION]: {
    src: 'assets/badges/foxg1-mutation.svg',
    alt: 'FOXG1 mutation',
    tooltipKey: 'BADGE.TOOLTIP.FOXG1_MUTATION'
  },
  [DiagnosisTypeEnum.CDKL5_MUTATION]: {
    src: 'assets/badges/cdkl5-mutation.svg',
    alt: 'CDKL5 mutation',
    tooltipKey: 'BADGE.TOOLTIP.CDKL5_MUTATION'
  },
  [DiagnosisTypeEnum.UNKNOWN_MUTATION]: {
    src: 'assets/badges/unknown.svg',
    alt: 'Unknown mutation',
    tooltipKey: 'BADGE.TOOLTIP.UNKNOWN_MUTATION'
  }
};

@Component({
  selector: 'app-diagnosis-badges',
  standalone: true,
  imports: [
    CommonModule,
    MatTooltipModule,
    TranslateModule
  ],
  templateUrl: './diagnosis-badges.component.html',
  styleUrls: ['./diagnosis-badges.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class DiagnosisBadgesComponent implements OnChanges {
  @Input() mainDiagnosis?: IDiagnosis;
  @Input() size = 128;
  badges: Badge[] = [];

  constructor(public translate: TranslateService) {}

  ngOnChanges() {
    this.badges = [];
    const dx = this.mainDiagnosis;
    if (!dx) {
      return;
    }

    // push name‑based badge
    const nameBadge = NAME_BADGES[dx.diagnosis_name as DiagnosisNameEnum];
    if (nameBadge) {
      this.badges.push(nameBadge);
    }

    // Only push type-based badge if the mutation is known
    const type = dx.diagnosis_type as DiagnosisTypeEnum;
    if (type && type !== DiagnosisTypeEnum.UNKNOWN_MUTATION) {
      const typeBadge = TYPE_BADGES[type];
      if (typeBadge) {
        this.badges.push(typeBadge);
      }
    }
  }
}